import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Clock, CalendarPlus, Pause, Flag, UserPlus } from 'lucide-react';
import { ApptBadge, FLAG_LABEL, fmtWhen } from './util';
import { ClientSearch } from './ClientSearch';
import { PhoneHandoff } from './PhoneHandoff';
import { CARD, INPUT, LABEL, Btn, Pill, cx } from '../contacts/ui';

const Row = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-3 border-b border-[var(--line-2)] py-2.5 text-sm last:border-0" data-testid={testid}>
    <span className="shrink-0 text-[var(--muted)]">{label}</span><span className="min-w-0 text-right font-medium text-[var(--ink)]">{children}</span>
  </div>
);

const ApptEditor = ({ item, onSave }) => {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState('');
  if (!open) return <button type="button" onClick={() => setOpen(true)} data-testid="call-appt-open" className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--accent-ink)] hover:underline"><CalendarPlus className="h-3.5 w-3.5" />{item.next_appointment_at ? 'Change' : 'Book appointment'}</button>;
  return (
    <span className="mt-1 flex flex-col gap-1.5">
      <input type="datetime-local" value={v} onChange={(e) => setV(e.target.value)} data-testid="call-appt-input" className={cx(INPUT, 'py-1.5 text-xs')} />
      <span className="flex justify-end gap-1.5">
        {item.next_appointment_at && <Btn tone="danger" size="sm" onClick={() => { onSave(null); setOpen(false); }} data-testid="call-appt-clear">Clear</Btn>}
        <Btn tone="solid" size="sm" disabled={!v} onClick={() => { onSave(new Date(v).toISOString()); setOpen(false); }} data-testid="call-appt-save">Save</Btn>
      </span>
    </span>
  );
};

export const SidePanel = ({ session, item, headers, onAppt, onAdd, onPause, onFinish }) => {
  const [adding, setAdding] = useState(false);
  const profile = item.profile_url || (item.application_id ? `/onboarding/${item.application_id}` : null);
  return (
    <aside className="space-y-3" data-testid="call-side-panel">
      <section className={cx(CARD, 'p-4')}>
        <p className={LABEL}>Contact details</p>
        <Row label="Last call" testid="call-last-contact"><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-[var(--faint)]" />{item.last_contact_at ? `${fmtWhen(item.last_contact_at)}${item.last_outcome ? ` · ${item.last_outcome}` : ''}` : 'Never'}</span></Row>
        <Row label="Team member">{item.broker_name || '—'}</Row>
        {item.callback_at && <Row label="Callback" testid="call-callback-at">{fmtWhen(item.callback_at)}</Row>}
        {!!item.flags?.length && <Row label="Open items"><span className="flex flex-wrap justify-end gap-1">{item.flags.map((f) => <Pill key={f} className="bg-[var(--amber-bg)] text-[var(--amber-ink)]">{FLAG_LABEL[f]}</Pill>)}</span></Row>}
        {item.application_id && item.kind !== 'esign' && <Row label="Appointment"><span className="flex flex-col items-end gap-1"><ApptBadge at={item.next_appointment_at} testId="call-client-appointment" /><ApptEditor item={item} onSave={onAppt} /></span></Row>}
        {profile && <Link to={profile} target="_blank" data-testid="call-open-profile" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--accent-ink)] hover:underline">{item.kind === 'esign' ? 'Open document' : item.kind === 'contact' ? 'Open contact' : 'Open full profile'}<ExternalLink className="h-3.5 w-3.5" /></Link>}
      </section>
      <section className={cx(CARD, 'p-4')}>
        {adding ? <ClientSearch compact headers={headers} onAdd={(r) => onAdd(r.key)} onRemove={() => {}} isIn={(k) => session.items.some((i) => i.key === k)} />
          : <Btn tone="ghost" className="w-full" onClick={() => setAdding(true)} data-testid="call-add-contact-btn"><UserPlus className="h-4 w-4" />Add someone to this queue</Btn>}
      </section>
      <PhoneHandoff sid={session.id} headers={headers} className="hidden lg:inline-flex" />
      <div className="flex gap-2">
        <Btn tone="ghost" className="flex-1" onClick={onPause} data-testid="call-pause-btn"><Pause className="h-4 w-4" />Pause</Btn>
        <Btn tone="danger" className="flex-1 border border-[var(--line)]" onClick={onFinish} data-testid="call-finish-btn"><Flag className="h-4 w-4" />Finish</Btn>
      </div>
    </aside>
  );
};
